/**
 * Utilitários para manipulação segura do DOM
 * Previne erros de null/undefined ao acessar propriedades de elementos
 */

import { logger } from '../services/analytics';

/**
 * Scroll seguro para um elemento específico
 * @param selector - Seletor CSS do elemento
 * @param offset - Offset adicional (padrão: -50)
 * @param behavior - Comportamento do scroll (padrão: 'smooth')
 */
export const safeScrollToElement = (
  selector: string, 
  offset: number = -50, 
  behavior: ScrollBehavior = 'smooth'
): boolean => {
  try {
    const element = document.querySelector(selector);
    
    if (!element) {
      console.warn(`Elemento com seletor '${selector}' não encontrado`);
      return false;
    }

    const elementTop = (element as HTMLElement).offsetTop;
    window.scrollTo({ 
      top: elementTop + offset, 
      behavior 
    });
    
    return true;
  } catch (error) {
    logger.error('Erro ao fazer scroll para elemento', error as Error);
    return false;
  }
};

/**
 * Scroll seguro para o topo da página
 * @param behavior - Comportamento do scroll (padrão: 'smooth')
 */
export const safeScrollToTop = (behavior: ScrollBehavior = 'smooth'): void => {
  try {
    window.scrollTo({ top: 0, behavior });
  } catch (error) {
    logger.error('Erro ao fazer scroll para o topo', error as Error);
  }
};

/**
 * Obter propriedades de offset de forma segura
 * @param element - Elemento HTML
 * @returns Objeto com propriedades de offset ou null se elemento não existir
 */
export const getSafeOffset = (element: HTMLElement | null) => {
  if (!element) {
    return null;
  }

  try {
    return {
      top: element.offsetTop,
      left: element.offsetLeft,
      width: element.offsetWidth,
      height: element.offsetHeight
    };
  } catch (error) {
    logger.error('Erro ao obter offset do elemento', error as Error);
    return null;
  }
};

/**
 * Obter propriedades de client de forma segura
 * @param element - Elemento HTML
 * @returns Objeto com propriedades de client ou null se elemento não existir
 */
export const getSafeClient = (element: HTMLElement | null) => {
  if (!element) {
    return null;
  }

  try {
    return {
      width: element.clientWidth,
      height: element.clientHeight,
      top: element.clientTop,
      left: element.clientLeft
    };
  } catch (error) {
    logger.error('Erro ao obter client do elemento', error as Error);
    return null;
  }
};

/**
 * Obter propriedades de scroll de forma segura
 * @param element - Elemento HTML (padrão: document.documentElement)
 * @returns Objeto com propriedades de scroll ou null se elemento não existir
 */
export const getSafeScroll = (element: HTMLElement | Document = document.documentElement) => {
  if (!element) {
    return null;
  }

  try {
    // Verificar se é um elemento HTMLElement ou Document
    const isHTMLElement = 'offsetTop' in element;
    
    if (isHTMLElement) {
      const htmlElement = element as HTMLElement;
      return {
        top: htmlElement.scrollTop || 0,
        left: htmlElement.scrollLeft || 0,
        width: htmlElement.scrollWidth || 0,
        height: htmlElement.scrollHeight || 0
      };
    } else {
      // Para Document, usar documentElement
      const doc = element as Document;
      return {
        top: doc.documentElement.scrollTop || 0,
        left: doc.documentElement.scrollLeft || 0,
        width: doc.documentElement.scrollWidth || 0,
        height: doc.documentElement.scrollHeight || 0
      };
    }
  } catch (error) {
    logger.error('Erro ao obter scroll do elemento', error as Error);
    return null;
  }
};

/**
 * Verificar se um elemento está visível na viewport
 * @param element - Elemento HTML
 * @param threshold - Threshold de visibilidade (0-1)
 * @returns true se elemento estiver visível
 */
export const isElementVisible = (element: HTMLElement | null, threshold: number = 0.1): boolean => {
  if (!element) {
    return false;
  }

  try {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    return (
      rect.top < windowHeight * (1 - threshold) &&
      rect.bottom > windowHeight * threshold &&
      rect.left < windowWidth &&
      rect.right > 0
    );
  } catch (error) {
    logger.error('Erro ao verificar visibilidade do elemento', error as Error);
    return false;
  }
};

/**
 * Adicionar listener de scroll com debounce
 * @param callback - Função de callback
 * @param delay - Delay do debounce em ms (padrão: 100)
 * @returns Função para remover o listener
 */
export const addScrollListener = (
  callback: () => void, 
  delay: number = 100
): (() => void) => {
  let timeoutId: NodeJS.Timeout;

  const debouncedCallback = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };

  window.addEventListener('scroll', debouncedCallback, { passive: true });

  return () => {
    clearTimeout(timeoutId);
    window.removeEventListener('scroll', debouncedCallback);
  };
};

/**
 * Adicionar listener de resize com debounce
 * @param callback - Função de callback
 * @param delay - Delay do debounce em ms (padrão: 250)
 * @returns Função para remover o listener
 */
export const addResizeListener = (
  callback: () => void, 
  delay: number = 250
): (() => void) => {
  let timeoutId: NodeJS.Timeout;

  const debouncedCallback = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };

  window.addEventListener('resize', debouncedCallback, { passive: true });

  return () => {
    clearTimeout(timeoutId);
    window.removeEventListener('resize', debouncedCallback);
  };
};
